// Menú por rol: mismas etiquetas, íconos y orden del prototipo TEO V5.2.1 (roleNav + navForSessionV52),
// sin las opciones que quedan fuera de la Etapa 1 (ver docs/fase1/DECISIONES.md).

export type RoleCode = 'manager' | 'warehouse' | 'store' | 'authorizer' | 'bodega_ops';

export type ViewKey =
  | 'dashboard'
  | 'warehouse'
  | 'warehouseSales'
  | 'inventory'
  | 'products'
  | 'cleanup'
  | 'stores'
  | 'audit'
  | 'movements'
  | 'config'
  | 'pos'
  | 'receiveStore'
  | 'masterEntry'
  | 'masterDispatch'
  | 'cash'
  | 'purchaseRequests'
  | 'purchaseAuth'
  | 'supply'
  | 'credits'
  | 'reports';

export interface NavItem {
  view: ViewKey;
  icon: string;
}

/**
 * Permiso que abre cada módulo. Gerencia puede habilitar o quitar módulos por usuario (migración 0031);
 * el menú se arma con los permisos efectivos, y el servidor exige el mismo permiso en cada ruta, así que
 * quitar un módulo no es esconder un botón: la API también lo rechaza.
 */
export const VIEW_PERMISSION: Record<ViewKey, string[]> = {
  dashboard: ['dashboard.view'],
  warehouse: ['inventory.entry'],
  warehouseSales: ['sale.create'],
  inventory: ['inventory.view'],
  supply: ['supply.view'],
  products: ['catalog.view'],
  cleanup: ['catalog.edit'],
  stores: ['config.manage'],
  // La Caja la abren dos permisos distintos: Gerencia y Autorizador la consultan, el local entrega efectivo.
  cash: ['cash.view', 'cash.handover'],
  credits: ['credit.view'],
  audit: ['audit.view'],
  reports: ['reports.view'],
  purchaseAuth: ['purchase.authorize'],
  purchaseRequests: ['purchase.request'],
  movements: ['kardex.view'],
  config: ['config.manage'],
  pos: ['sale.create'],
  receiveStore: ['shipment.receive'],
  masterEntry: ['inventory.own_entry'],
  masterDispatch: ['shipment.dispatch'],
};

const NAV: Record<RoleCode, NavItem[]> = {
  manager: [
    { view: 'dashboard', icon: '◫' },
    { view: 'warehouse', icon: '▣' },
    { view: 'warehouseSales', icon: '▦' },
    { view: 'inventory', icon: '▤' },
    { view: 'supply', icon: '◐' },
    { view: 'products', icon: '◈' },
    { view: 'cleanup', icon: '⌫' },
    { view: 'stores', icon: '⌂' },
    { view: 'cash', icon: '$' },
    { view: 'credits', icon: '◇' },
    { view: 'audit', icon: '◎' },
    { view: 'reports', icon: '▥' },
    { view: 'purchaseAuth', icon: '✓' },
    { view: 'movements', icon: '≡' },
    { view: 'config', icon: '⚙' },
  ],
  warehouse: [
    { view: 'warehouse', icon: '▣' },
    { view: 'warehouseSales', icon: '▦' },
    { view: 'cash', icon: '$' },
    { view: 'credits', icon: '◇' },
    { view: 'inventory', icon: '▤' },
    { view: 'supply', icon: '◐' },
    { view: 'purchaseRequests', icon: '＋' },
    { view: 'movements', icon: '≡' },
  ],
  bodega_ops: [
    { view: 'dashboard', icon: '◫' },
    { view: 'warehouse', icon: '▣' },
    { view: 'warehouseSales', icon: '▦' },
    { view: 'inventory', icon: '▤' },
    { view: 'supply', icon: '◐' },
    { view: 'products', icon: '◈' },
    { view: 'cleanup', icon: '⌫' },
    { view: 'cash', icon: '$' },
    { view: 'credits', icon: '◇' },
    { view: 'reports', icon: '▥' },
    { view: 'purchaseAuth', icon: '✓' },
    { view: 'purchaseRequests', icon: '＋' },
    { view: 'movements', icon: '≡' },
  ],
  store: [
    { view: 'pos', icon: '⌁' },
    { view: 'receiveStore', icon: '↓' },
    { view: 'cash', icon: '$' },
    { view: 'credits', icon: '◇' },
    { view: 'inventory', icon: '▤' },
    { view: 'supply', icon: '◐' },
    { view: 'purchaseRequests', icon: '＋' },
  ],
  authorizer: [
    { view: 'purchaseAuth', icon: '✓' },
    { view: 'reports', icon: '▥' },
    { view: 'inventory', icon: '▤' },
    { view: 'supply', icon: '◐' },
    { view: 'cash', icon: '$' },
    { view: 'credits', icon: '◇' },
    { view: 'movements', icon: '≡' },
  ],
};

/** Pestañas de la vista Bodega (sin Importación China, que queda para la Etapa 2). */
export const WAREHOUSE_TABS = ['receive', 'dispatch', 'direct', 'transit'] as const;

export interface LocationCapabilities {
  /** Ubicación que distribuye producción propia (hoy Masters 85). */
  canDistribute?: boolean;
}

export function navForRole(role: RoleCode, location: LocationCapabilities = {}): NavItem[] {
  const items = [...NAV[role]];
  if (role === 'store' && location.canDistribute) {
    // Mismas posiciones que el prototipo: después de "Recibir" (Caja, de la Etapa 2, queda después).
    items.splice(2, 0, { view: 'masterEntry', icon: '＋' }, { view: 'masterDispatch', icon: '⇄' });
  }
  return items;
}

/** Todos los módulos con su ícono, en el orden en que los muestra el prototipo. */
const TODOS: NavItem[] = [
  { view: 'dashboard', icon: '◫' }, { view: 'warehouse', icon: '▣' }, { view: 'warehouseSales', icon: '▦' }, { view: 'pos', icon: '⌁' },
  { view: 'receiveStore', icon: '↓' }, { view: 'masterEntry', icon: '＋' }, { view: 'masterDispatch', icon: '⇄' },
  { view: 'inventory', icon: '▤' }, { view: 'supply', icon: '◐' }, { view: 'products', icon: '◈' },
  { view: 'cleanup', icon: '⌫' }, { view: 'stores', icon: '⌂' }, { view: 'cash', icon: '$' },
  { view: 'credits', icon: '◇' }, { view: 'audit', icon: '◎' }, { view: 'reports', icon: '▥' },
  { view: 'purchaseAuth', icon: '✓' }, { view: 'purchaseRequests', icon: '＋' },
  { view: 'movements', icon: '≡' }, { view: 'config', icon: '⚙' },
];

/**
 * Menú del usuario: el de su rol, recortado a los módulos que tiene, más los que Gerencia le habilitó
 * a mano (que su rol no trae y por eso hay que añadirlos). Sin lista de permisos se devuelve el del rol
 * tal cual, que es como se comportaba antes de los módulos por usuario.
 */
export function navForUser(
  role: RoleCode,
  permissions: string[] | undefined,
  location: LocationCapabilities = {},
  granted: string[] = [],
): NavItem[] {
  const items = navForRole(role, location);
  if (!permissions) return items;
  const puede = (v: ViewKey) => VIEW_PERMISSION[v].some((p) => permissions.includes(p));
  const base = items.filter((i) => puede(i.view));
  const extra = TODOS.filter((i) => !base.some((b) => b.view === i.view) && VIEW_PERMISSION[i.view].some((p) => granted.includes(p)));
  return [...base, ...extra];
}
